import '@akoom-tech/mobiscroll-react/dist/css/mobiscroll.min.css';
import '@/theme/mobiscroll.css';
//
import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
// mobiscroll
import {
  CalendarNext,
  CalendarPrev,
  CalendarToday,
  Datepicker,
  Eventcalendar,
  MbscEventClickEvent,
  MbscEventcalendarView,
  formatDate,
} from '@akoom-tech/mobiscroll-react';
import { Button, Card, Stack } from '@mui/material';
//
import { ProviderTimeOffRequest, ProviderAvailabilityTable } from '..';
// API
import { getProviderAllDaysOff } from '../../api';
// utils
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { buildProviderAvailabilityCalendarEvents } from '../../utils';
import { SkeletonLoadingTableData } from '@/components/skeleton';

export default function ProviderAvailability() {
  const { userId } = useParams();
  const { errorMsg } = useSnackbarMsg();
  const [selectedTimeOffId, setSelectedTimeOffId] = useState<string | null>(
    null
  );

  // =================
  //      QUERIES
  // =================
  // TODO: should we add a loading indicator to the calendar?
  const { data: providerDaysOff, isLoading: isDaysOffDataLoading } = useQuery({
    queryKey: ['providerTimeoff', userId],
    queryFn: () => getProviderAllDaysOff(String(userId)),
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching days off`);
    },
    enabled: !!userId,
  });

  // ======================
  //       EDIT MODE
  // ======================
  const [editMode, setEditMode] = useState(false);
  const [currentTimeOffId, setCurrentTimeOffId] = useState<string | null>(null);
  const handleEdit = (id: string) => {
    setEditMode(true);
    setCurrentTimeOffId(id);
    setSelectedTimeOffId(id);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  const exitEditMode = () => {
    setEditMode(false);
    setCurrentTimeOffId(null);
  };

  // ======================
  //    CALENDAR CONFIG
  // ======================

  const calendarViewConfig: MbscEventcalendarView = {
    timeline: {
      type: 'week',
      size: 1,
      startTime: '06:00',
      endTime: '23:00',
      resolutionVertical: 'day',
      resolutionHorizontal: 'hour',
    },
  };

  const [myRefDate, setMyRefDate] = useState<Date>();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [rangeVal, setRangeVal] = useState<any[]>([]);
  const [buttonText, setButtonText] = useState<string>('');
  const [visibleRange, setVisibleRange] = useState<{
    start: Date;
    end: Date;
  } | null>(null);

  const startDate = useRef<Date | null>(null);
  const endDate = useRef<Date | null>(null);

  const calendarEvents = useMemo(
    () =>
      buildProviderAvailabilityCalendarEvents(providerDaysOff || [], {
        rangeEnd: visibleRange?.end,
        rangeStart: visibleRange?.start,
        selectedTimeOffId,
      }),
    [providerDaysOff, selectedTimeOffId, visibleRange]
  );

  const buttonProps = useMemo(() => {
    const content = <span className="mbsc-calendar-title">{buttonText}</span>;
    return {
      children: content,
      className: 'mbsc-calendar-button',
      variant: 'flat',
    };
  }, [buttonText]);

  // returns the number of days between two dates
  const getNrDays = useCallback(
    (start: any, end: any) =>
      Math.round(
        Math.abs((end.setHours(0) - start.setHours(0)) / (24 * 60 * 60 * 1000))
      ) + 1,
    []
  );

  // returns the formatted date
  const getFormattedRange = useCallback(
    (start: any, end: any) =>
      formatDate('MMM D, YYYY', new Date(start)) +
      (end && getNrDays(start, end) > 1
        ? ' - ' + formatDate('MMM D, YYYY', new Date(end))
        : ''),
    [getNrDays]
  );

  const handleDateChangeInHeader = useCallback((args: any) => {
    const date = args.value;
    setRangeVal(date);
    if (date[0] && date[1]) {
      startDate.current = date[0];
      endDate.current = date[1];
    }
  }, []);

  const handleCloseInHeader = useCallback(() => {
    if (startDate.current && endDate.current) {
      // navigate the calendar
      setCurrentDate(startDate.current);
      // set calendar view
      setMyRefDate(startDate.current);
    }
    setRangeVal([startDate.current, endDate.current]);
  }, []);

  const handlePageLoading = useCallback(
    (args: any) => {
      const sDate = args.firstDay;
      const end = args.lastDay;
      const eDate = new Date(
        end.getFullYear(),
        end.getMonth(),
        end.getDate() - 1,
        0
      );
      startDate.current = sDate;
      endDate.current = eDate;
      setTimeout(() => {
        // Mobiscroll can call onPageLoading while rendering its internals.
        // Defer React state updates to avoid cross-component render updates.
        setVisibleRange((currentRange) => {
          if (
            currentRange &&
            currentRange.start.getTime() === sDate.getTime() &&
            currentRange.end.getTime() === eDate.getTime()
          ) {
            return currentRange;
          }

          return { start: sDate, end: eDate };
        });
        // set button text
        setButtonText(getFormattedRange(sDate, eDate));
        // set range value
        setRangeVal([sDate, eDate]);
        // navigate the calendar
        setCurrentDate(sDate);
      });
    },
    [getFormattedRange]
  );

  const handleCalendarEventClick = useCallback((args: MbscEventClickEvent) => {
    const event = args.event as { id?: string | number; offdayId?: number };
    const offdayId = event.offdayId ?? event.id;

    if (offdayId) {
      setSelectedTimeOffId(String(offdayId));
    }
  }, []);

  const customHeaderWithNavButtons = () => (
    <Stack
      sx={{
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <div>
        <Datepicker
          select="range"
          display="anchored"
          showOverlay={false}
          touchUi={true}
          buttons={[]}
          inputComponent={Button}
          inputProps={buttonProps}
          onClose={handleCloseInHeader}
          onChange={handleDateChangeInHeader}
          value={rangeVal}
        />
      </div>
      <div className="md-custom-range-view-controls">
        <CalendarPrev />
        <CalendarToday />
        <CalendarNext />
      </div>
    </Stack>
  );

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="row" spacing={2}>
        <ProviderTimeOffRequest
          editMode={editMode}
          currentTimeOffId={currentTimeOffId}
          exitEditMode={exitEditMode}
        />
        <Card
          sx={{
            p: 2,
            width: '100%',
            '& .mbsc-timeline-row': { height: 35 },
            '& .provider-availability-event-selected': {
              boxShadow: (theme) =>
                `0 0 0 2px ${theme.palette.primary.main}`,
              zIndex: 2,
            },
          }}
        >
          {isDaysOffDataLoading || !providerDaysOff ? (
            <SkeletonLoadingTableData />
          ) : (
            <Eventcalendar
              selectedDate={currentDate}
              renderHeader={customHeaderWithNavButtons}
              view={calendarViewConfig}
              firstDay={1}
              data={calendarEvents}
              dataTimezone="bst"
              onEventClick={handleCalendarEventClick}
              onPageLoading={handlePageLoading}
              refDate={myRefDate}
              theme="ios"
              themeVariant="light"
              rowHeight={'equal'}
            />
          )}
        </Card>
      </Stack>
      <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
        <ProviderAvailabilityTable
          handleEdit={handleEdit}
          selectedTimeOffId={selectedTimeOffId}
          onSelectTimeOff={setSelectedTimeOffId}
        />
      </Stack>
    </Stack>
  );
}
